import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import Header from "@/components/Header";
import ScrollToTop from "@/components/ScrollToTop";
import RouteHistoryTracker from "@/components/RouteHistoryTracker";
import GlobalConsultationListener from "@/components/home/GlobalConsultationListener";

import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Footer from "@/components/Footer";
// GeoGate removed — CN/HK/MO are now blocked at the Cloudflare edge
// via a WAF custom rule, so requests never reach the app. The
// server-side `_shared/geoBlock.ts` remains as a defense-in-depth
// layer for edge functions.

// Retry dynamic imports once, then force a one-time hard reload if the chunk
// is gone (typical after a redeploy invalidates old hashed filenames).
function lazyWithReload<T extends { default: React.ComponentType<any> }>(
  factory: () => Promise<T>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err: any) {
      const msg = String(err?.message || err);
      const isChunkError =
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg) ||
        /ChunkLoadError/i.test(msg);
      if (isChunkError && typeof window !== "undefined") {
        const KEY = "__chunk_reload__";
        if (!sessionStorage.getItem(KEY)) {
          sessionStorage.setItem(KEY, "1");
          window.location.reload();
          return new Promise<T>(() => {}); // never resolves; reload in flight
        }
      }
      throw err;
    }
  });
}

// Clear the reload guard on successful load
if (typeof window !== "undefined") {
  window.addEventListener("load", () => sessionStorage.removeItem("__chunk_reload__"));
}


// Lazy-load heavy pages for faster initial paint
const Work = lazyWithReload(() => import("./pages/Work"));
const About = lazyWithReload(() => import("./pages/About"));
const DesignPage = lazyWithReload(() => import("./pages/Design"));
const ProjectDetail = lazyWithReload(() => import("./pages/ProjectDetail"));
// /platform retired — redirects to /system#platform (see route below).
const LazyLabs = lazyWithReload(() => import("./pages/Labs"));
const LazySystem = lazyWithReload(() => import("./pages/System"));
const LazySolutions = lazyWithReload(() => import("./pages/Solutions"));
const LazyContact = lazyWithReload(() => import("./pages/Contact"));
const LazyTalent = lazyWithReload(() => import("./pages/Talent"));
const LazyTerms = lazyWithReload(() => import("./pages/legal/Terms"));
const LazyPrivacy = lazyWithReload(() => import("./pages/legal/Privacy"));
// Dev-only: internal QA route for button tokens. Stripped from production builds.
const LazyDebugButtons = import.meta.env.DEV
  ? lazyWithReload(() => import("./pages/DebugButtons"))
  : null;
const LazyCircadianLighting = lazyWithReload(() => import("./pages/insights/CircadianLighting"));
const LazyUnsubscribe = lazyWithReload(() => import("./pages/Unsubscribe"));
const LazyInternalLogin = lazyWithReload(() => import("./pages/internal/Login"));
const LazyInternalGuard = lazyWithReload(() => import("./pages/internal/InternalGuard"));
const LazyLeadsList = lazyWithReload(() => import("./pages/internal/LeadsList"));
const LazyLeadDetail = lazyWithReload(() => import("./pages/internal/LeadDetail"));
const LazyAssetsLibrary = lazyWithReload(() => import("./pages/internal/AssetsLibrary"));
const LazyInternalResetPassword = lazyWithReload(() => import("./pages/internal/ResetPassword"));
// Temporary Track B closure page. Safe to remove: delete this import,
// the route below, and src/pages/internal/OxbowVerify.tsx.
const LazyOxbowVerify = lazyWithReload(() => import("./pages/internal/OxbowVerify"));
const LazyPassJobs = lazyWithReload(() => import("./pages/internal/PassJobs"));
const LazyAnalysisCohort = lazyWithReload(() => import("./pages/internal/AnalysisCohort"));

const queryClient = new QueryClient();

const PublicLayout = () => (
  <>
    <ScrollToTop />
    <RouteHistoryTracker />
    <Header />
    <GlobalConsultationListener />
    
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/work" element={<Work />} />
        <Route path="/work/:slug" element={<ProjectDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/design" element={<DesignPage />} />
        <Route path="/labs" element={<LazyLabs />} />
        {/* Retired routes, consolidated into /system */}
        <Route path="/platform" element={<Navigate to="/system#platform" replace />} />
        <Route path="/ecosystem" element={<Navigate to="/system" replace />} />
        <Route path="/manufacturing" element={<Navigate to="/system#products" replace />} />
        <Route path="/controls" element={<Navigate to="/system#controls" replace />} />

        <Route path="/system" element={<LazySystem />} />
        <Route path="/solutions" element={<LazySolutions />} />
        <Route path="/contact" element={<LazyContact />} />
        <Route path="/talent" element={<LazyTalent />} />
        <Route path="/terms" element={<LazyTerms />} />
        <Route path="/privacy" element={<LazyPrivacy />} />
        <Route path="/insights/circadian-lighting" element={<LazyCircadianLighting />} />
        <Route path="/unsubscribe" element={<LazyUnsubscribe />} />
        {import.meta.env.DEV && LazyDebugButtons && (
          <Route path="/debug/buttons" element={<LazyDebugButtons />} />
        )}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    <Footer />
  </>
);

const Layout = () => (
  <Suspense fallback={<div className="min-h-screen bg-background" />}>
    <Routes>
      {/* Internal dashboard — no site chrome, noindex enforced per-page */}
      <Route path="/internal/login" element={<LazyInternalLogin />} />
      <Route path="/internal/reset-password" element={<LazyInternalResetPassword />} />
      <Route path="/internal/leads" element={<LazyInternalGuard><LazyLeadsList /></LazyInternalGuard>} />
      <Route path="/internal/leads/:id" element={<LazyInternalGuard><LazyLeadDetail /></LazyInternalGuard>} />
      <Route path="/internal/assets" element={<LazyInternalGuard><LazyAssetsLibrary /></LazyInternalGuard>} />
      <Route path="/internal/oxbow-verify" element={<LazyInternalGuard><LazyOxbowVerify /></LazyInternalGuard>} />
      <Route path="/internal/pass-jobs" element={<LazyInternalGuard><LazyPassJobs /></LazyInternalGuard>} />
      <Route path="/internal/analysis-cohort" element={<LazyInternalGuard><LazyAnalysisCohort /></LazyInternalGuard>} />

      <Route path="/internal/*" element={<Navigate to="/internal/login" replace />} />
      <Route path="/*" element={<PublicLayout />} />
    </Routes>
  </Suspense>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Layout />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
